/**
 * Комиссия выплаты и «сколько можно выплатить с этого остатка».
 *
 * Формула комиссии одна и та же во всём кабинете:
 *
 *     fee(X) = fixedPerTransaction + max(X × cost / 100, costFixedMinimum)
 *
 * Она уже была рассыпана по страницам реестров копипастой; здесь она одна,
 * чтобы карточка «Доступно к выплатам» и итог по загруженному файлу не
 * разъезжались.
 */

/**
 * Структура комиссии выплаты у терминала.
 *
 * @param {object|null} terminal
 * @returns {object|null}
 */
export function getPayoutFee(terminal) {
    if (!terminal || !terminal.fee) {
        return null;
    }

    return terminal.fee.payout || null;
}

/**
 * Комиссия за одну выплату суммой amount.
 *
 * @param {object|null} fee
 * @param {number|string} amount
 * @returns {number}
 */
export function calcFee(fee, amount) {
    if (!fee) {
        return 0;
    }

    const amountNum = Number(amount) || 0;
    const cost = Number(fee.cost) || 0;
    const min = Number(fee.costFixedMinimum) || 0;
    const fixed = Number(fee.fixedPerTransaction) || 0;

    return Math.max(amountNum * (cost / 100), min) + fixed;
}

/**
 * Максимальная сумма X, которую можно выплатить одной строкой при остатке
 * balance, чтобы после списания X вместе с комиссией остаток был не меньше
 * нуля.
 *
 * Списание: debit(X) = X + fixed + max(X × cost/100, min). Отсюда два случая:
 *
 *   1) процентная комиссия не меньше минимума: X ≤ (balance − fixed) / (1 + cost/100)
 *   2) сработал минимум:                       X ≤ balance − fixed − min
 *
 * Берём меньшее из двух — тогда сумма проходит при любом раскладе.
 *
 * @param {number|string} balance
 * @param {object|null} fee
 * @returns {number}
 */
export function calcAvailableForFee(balance, fee) {
    const balanceNum = Number(balance) || 0;

    if (balanceNum <= 0) {
        return 0;
    }

    if (!fee) {
        return balanceNum;
    }

    const cost = Number(fee.cost) || 0;
    const min = Number(fee.costFixedMinimum) || 0;
    const fixed = Number(fee.fixedPerTransaction) || 0;

    if (cost === 0 && min === 0 && fixed === 0) {
        return balanceNum;
    }

    const availMin = balanceNum - fixed - min;

    if (cost === 0) {
        return Math.max(0, availMin);
    }

    const availPercent = (balanceNum - fixed) / (1 + cost / 100);

    return Math.max(0, Math.min(availPercent, availMin));
}

/**
 * Сколько можно выплатить через этот терминал с указанного остатка.
 *
 * @param {object|null} terminal
 * @param {number|string} balance
 * @returns {number}
 */
export function calcAvailableForTerminal(terminal, balance) {
    return calcAvailableForFee(balance, getPayoutFee(terminal));
}



// WEBPACK FOOTER //
// ./src/Services/feeCalculator.js